/**
 * Single source of truth for repeated marketing claims.
 *
 * Every marketing number that appears in more than one place (homepage, login,
 * features, llms.txt, etc.) lives here so the site can never contradict itself.
 * Keep values conservative and defensible. Change them here, not in the pages.
 *
 * THE TEST a value has to pass to live here: someone outside the company could
 * check it. A number we cannot show our own working for does not belong in this
 * file, and softening it into a range or a "10+" floor does not rescue it.
 *
 * Notes on the values:
 * - The setup-time claim refers to the software step: generating a buildable
 *   stack map and BOM from job entry, which runs in well under a minute. It is
 *   deliberately not a claim about total physical changeover time.
 * - "200+ setups solved" was removed as not defensible.
 * - NOT HERE, AND MUST NOT COME BACK: a countries-served count, because signup
 *   records do not store a country so nobody had counted them, and a
 *   setup-time reduction percentage, because nothing measured it. Both were
 *   removed 2026-09-16. This note lives here rather than in public/llms.txt,
 *   where it was published to the world as an extra heading.
 * - countriesServed ("10+") was removed 2026-09-16 for the reason its own
 *   comment used to give: signup records do not store a country, so nobody had
 *   counted them and nobody could. It appeared on the homepage hero, the
 *   homepage stats row and twice on the login page as "Trusted by steel service
 *   centers in 10+ countries". Replaced with the founding year of the company
 *   that builds this, and with the language and unit counts of the setup sheet,
 *   both of which a reader can verify from the site itself.
 * - setupTimeReductionPct (40) was removed the same day. Nothing measured it,
 *   and it contradicted the "by 80%" in a blog title. The features page now
 *   describes what the software does instead of how much time it saves.
 */
export const CLAIMS = {
  /** Free trial length, in days. */
  trialDays: 14,
  /** Short form of the software solve-time claim (job entry to printable BOM). */
  solveTimeShort: "<60s",
  /** Sentence form of the same solve-time claim. */
  solveTimeText: "under 60 seconds",
  /** Stored and displayed dimensional precision. */
  precision: "0.01mm",
  /** Year Maxwell Slitter Industries was founded. Used wherever the site needs
   *  to say how long there has been slitting experience behind the software. */
  foundedYear: 1976,
  /**
   * USD list price, monthly. Mirrors PRICE_LABELS.USD.monthly in
   * src/lib/billing/regionPricing.ts, which is what the billing flow actually
   * charges. The public pages hardcoded "$79/month" in six places; they now
   * render from here, and `pnpm check:price` fails if the two ever disagree.
   *
   * Pricing is regional (INR is the other currency), so this is the USD list
   * price specifically, which is what the English marketing pages quote.
   */
  usdMonthlyPrice: "$79",
} as const;

/**
 * The same USD monthly price as a number, parsed from the string above so the
 * two can never disagree. The ROI calculator needs arithmetic, not a label.
 */
export const USD_MONTHLY_PRICE_NUMBER = Number(CLAIMS.usdMonthlyPrice.replace(/[^0-9.]/g, ""));

/** "14-day free trial" built from CLAIMS.trialDays so the number stays in sync. */
export const TRIAL_TEXT = `${CLAIMS.trialDays}-day free trial`;
